import {
  ProximityPolicyRuleCategoryFilterFragment,
  useFindLocationsStatsFilterQuery,
  useFindSimulationItemsTotalByFilterQuery,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import { formatInteger } from '../common/formatHelper';
import useFormatter from '../common/useFormatter';
import PolicyFilterGroupList from '../components/policies/PolicyFilterGroupList';
import { PolicyStatContainer } from '../components/policies/PolicyStatContainer';
import RuleSectionContainer from '../components/policies/RuleSectionContainer';
import { Stat } from '../components/stats/Stat';
import { getPolicyMatchInput } from '../policyFilters/policyFilter.helper';
import {
  getLocationFilterConfigCommon,
  getProductFilterConfigCommon,
} from '../simulation/store/assignmentPolicy.default';
import { optimisationIsEditable } from '../simulation/store/optimisation.state';
import { simulationCurrent } from '../simulation/store/simulation.state';
import { sidebarStateByType } from '../store/sidebar.state';
import {
  proximityPolicyRule,
  proximityPolicySelectedIdentity,
} from './proximityPolicy.state';
import {
  ProximityPolicySelectedCategory,
  ProximityPolicySelectedIdentity,
} from './proximityPolicy.type';

export type ProximityPolicyCategoryProps = {
  title: string;
  ruleId: string;
  category: ProximityPolicySelectedCategory;
};

const ProximityPolicyCategory: React.FC<
  ProximityPolicyCategoryProps
> = props => {
  const { t } = useTranslation('simulation');
  const simulation = useRecoilValue(simulationCurrent);
  const { ruleId, category } = props;
  const [rule, updateRule] = useRecoilState(proximityPolicyRule(ruleId));
  const [selectedIdentity, setSelectedIdentity] = useRecoilState(
    proximityPolicySelectedIdentity,
  );
  const [policyEditPanelState, setPolicyEditPanelState] = useRecoilState(
    sidebarStateByType('sidebar-policy-storage-editor'),
  );
  const isDisabled = !useRecoilValue(optimisationIsEditable);
  // const checkResult = useRecoilValue(proximityPolicyCheckResult);
  const formatter = useFormatter();

  const assignmentsMatch =
    category === 'main' ? rule.mainCategory : rule.secondCategory;

  const {
    data: itemsTotalByFilter,
    loading: isItemsStatsLoading,
    error: itemsTotalByFilterError,
  } = useFindSimulationItemsTotalByFilterQuery({
    variables: {
      input: {
        simulationId: simulation.id,
        includeMatching: getPolicyMatchInput(assignmentsMatch.productsMatch),
        includeUnassigned: true,
      },
    },
  });

  const {
    data: locationsTotalByFilter,
    loading: isLocationsStatsLoading,
    error: locationsStatsError,
  } = useFindLocationsStatsFilterQuery({
    variables: {
      layoutId: simulation.layout.id,
      summary: {
        assignmentId: simulation.assignment.id,
        groupBy: [], //LayoutLocationsSummaryGroupBy.LOCATION_STATUS
        // uoms: _.map(locationCapacitySpec, spec => _.omit(spec, 'title')),
      },
      input: {
        includeMatching: getPolicyMatchInput(assignmentsMatch.locationsMatch),
      },
    },
  });

  function selectFilterIntersection(
    newIdentity: ProximityPolicySelectedIdentity,
  ) {
    const isClearing = _.isEqual(newIdentity, selectedIdentity);

    const newSelectedIdentity: ProximityPolicySelectedIdentity = isClearing
      ? null
      : newIdentity;

    setSelectedIdentity(newSelectedIdentity);

    // trigger  toggle filter editor panel
    if (!policyEditPanelState.isPinned) {
      setPolicyEditPanelState({
        ...policyEditPanelState,
        isCollapsed: isClearing,
      });
    }
  }

  function addLocationIntersection() {
    const id = nanoid();

    const newAssignmentsMatch: ProximityPolicyRuleCategoryFilterFragment = {
      ...(assignmentsMatch ?? {}),
      locationsMatch: {
        anyOf: [
          ...(assignmentsMatch?.locationsMatch?.anyOf ?? []),
          { id, allOf: [] },
        ],
      },
    };
    updateRule({
      ...rule,
      mainCategory:
        category === 'main' ? newAssignmentsMatch : rule.mainCategory,
      secondCategory:
        category === 'secondary' ? newAssignmentsMatch : rule.secondCategory,
    });

    selectFilterIntersection({
      ruleId,
      type: 'locations',
      filterId: id,
      category,
    });
  }

  function addProductIntersection() {
    const id = nanoid();

    const newAssignmentsMatch: ProximityPolicyRuleCategoryFilterFragment = {
      ...(assignmentsMatch ?? {}),
      productsMatch: {
        anyOf: [
          ...(assignmentsMatch?.productsMatch?.anyOf ?? []),
          { id, allOf: [] },
        ],
      },
    };
    updateRule({
      ...rule,
      mainCategory:
        category === 'main' ? newAssignmentsMatch : rule.mainCategory,
      secondCategory:
        category === 'secondary' ? newAssignmentsMatch : rule.secondCategory,
    });
    selectFilterIntersection({
      ruleId,
      type: 'items',
      filterId: id,
      category,
    });
  }

  const removeField = (groupId: string, fieldId: string) => {
    const newAssignmentsMatch: ProximityPolicyRuleCategoryFilterFragment = {
      ...(assignmentsMatch ?? {}),
      locationsMatch: {
        anyOf: _.map(assignmentsMatch.locationsMatch.anyOf, match => {
          if (match.id !== groupId) return match;
          return {
            id: groupId,
            allOf: match.allOf.filter(filter => filter.type !== fieldId),
          };
        }),
      },
      productsMatch: {
        anyOf: _.map(assignmentsMatch.productsMatch?.anyOf, match => {
          if (match.id !== groupId) return match;
          return {
            id: groupId,
            allOf: match.allOf.filter(filter => filter.type !== fieldId),
          };
        }),
      },
    };

    updateRule({
      ...rule,
      mainCategory:
        category === 'main' ? newAssignmentsMatch : rule.mainCategory,
      secondCategory:
        category === 'secondary' ? newAssignmentsMatch : rule.secondCategory,
    });
  };

  function removeIntersection(groupId: string) {
    const newAssignmentsMatch: ProximityPolicyRuleCategoryFilterFragment = {
      ...(assignmentsMatch ?? {}),
      locationsMatch: {
        anyOf: _.filter(
          assignmentsMatch.locationsMatch?.anyOf,
          fg => fg.id !== groupId,
        ),
      },
      productsMatch: {
        anyOf: _.filter(
          assignmentsMatch.productsMatch?.anyOf,
          fg => fg.id !== groupId,
        ),
      },
    };

    updateRule({
      ...rule,
      mainCategory:
        category === 'main' ? newAssignmentsMatch : rule.mainCategory,
      secondCategory:
        category === 'secondary' ? newAssignmentsMatch : rule.secondCategory,
    });
    if (groupId === selectedIdentity?.filterId) {
      selectFilterIntersection(null);
    }
  }
  // const ruleCheckResult = _.find(
  //   checkResult?.result?.rules,
  //   r => r.id === ruleId,
  // );
  // const match =
  //   category === 'source'
  //     ? ruleCheckResult?.srcMatch
  //     : ruleCheckResult?.destMatch;

  const isSelectedRule = selectedIdentity?.ruleId === ruleId;
  const isSelectedGroup =
    isSelectedRule && selectedIdentity?.category === props.category;
  const isSelectedProduct =
    isSelectedGroup && selectedIdentity?.type === 'items';
  const isSelectedLocation =
    isSelectedGroup && selectedIdentity?.type === 'locations';
  const isLoading = false; //checkResult?.status === AsyncLoadStatus.Loading;
  const hasIssue = false; //ruleCheckResult?.hasValidSwaps === false;
  const filteredItemsCount = formatInteger(
    itemsTotalByFilter?.findSimulationItemsByFilter?.totalCount ?? 0,
  );
  const filteredLocationsCount = formatInteger(
    locationsTotalByFilter?.layout?.locationsByFilter?.summary?.[0]
      ?.locationCount ?? 0,
  );
  const filteredAssignmentItemsCount = formatInteger(
    0, //match?.assignmentItemCount ?? 0,
  );
  const filteredEmptyLocationCount = formatInteger(
    0, //match?.emptyLocationCount ?? 0,
  );
  return (
    <div data-component="ProximityPolicyCategory" className={'mx-1 mb-4'}>
      <div className="text-menu-active px-2 pb-2 pt-2 text-sm uppercase">
        {props.title}
      </div>
      <div
        className={
          'border-menu-500 bg-app-panel/70 rounded border p-2 shadow-xl'
        }
      >
        <div className="divide-menu-300/50 mb-2 flex flex-col divide-x lg:flex-row">
          {/* <Stat
            hasNoPadding
            className={`bg-opacity-0 px-1 py-1 xl:px-2`}
            titleClassName="text-sm"
            //hasIssue={hasIssue}
            title={t`Item Stock`}
            value={filteredAssignmentItemsCount}
            inPanelMode
            inFilterStat
            toggleTable
            isCheckLoading={isLoading}
          /> */}
          {/* {showEmptyLocationsStats && (
            <Stat
              hasNoPadding
              className={`bg-opacity-0 px-1 py-1 xl:px-2`}
              titleClassName="text-sm"
              hasIssue={hasIssue}
              title={t`Empty Locations`}
              value={filteredEmptyLocationCount}
              inPanelMode
              inFilterStat
              toggleTable
              isCheckLoading={isLoading}
            />
          )} */}
        </div>

        {/**
         * ITEMS RELATED SECTION
         */}
        <div className="space-y-2">
          <RuleSectionContainer
            hasIssue={hasIssue}
            isSelected={isSelectedProduct}
          >
            <PolicyStatContainer>
              <Stat
                hasNoPadding
                className={`bg-opacity-0 px-1 py-1 xl:px-2`}
                hasIssue={hasIssue}
                title={t`Items`}
                value={filteredItemsCount}
                inPanelMode
                isActionable
                inFilterStat
                toggleTable
                isCheckLoading={isItemsStatsLoading}
                isSelected={isSelectedProduct}
                onClick={() =>
                  setSelectedIdentity(
                    isSelectedProduct
                      ? null
                      : {
                          ruleId,
                          filterId: null,
                          type: 'items',
                          category,
                        },
                  )
                }
              />
            </PolicyStatContainer>
            <PolicyFilterGroupList
              isDisabled={isDisabled}
              filterSets={_.map(
                assignmentsMatch?.productsMatch?.anyOf,
                filterGroup => ({
                  id: filterGroup.id,
                  allOf: [...filterGroup.allOf],
                }),
              )}
              config={getProductFilterConfigCommon(t, formatter)}
              selectedId={selectedIdentity?.filterId}
              onAddGroup={addProductIntersection}
              onDeleteField={removeField}
              onSelectGroup={(id, isSelected) =>
                selectFilterIntersection({
                  ruleId,
                  filterId: id,
                  type: 'items',
                  category,
                })
              }
              onDeleteGroup={removeIntersection}
            />
          </RuleSectionContainer>

          {/**
           * Locations  RELATED SECTION
           */}
          <RuleSectionContainer
            hasIssue={hasIssue}
            isSelected={isSelectedLocation}
          >
            <PolicyStatContainer>
              <Stat
                hasNoPadding
                className={`bg-opacity-0 px-1 py-1 xl:px-2`}
                hasIssue={hasIssue}
                title={t`Locations`}
                // value={formatInteger(props.locationCount[1])}
                value={filteredLocationsCount}
                inPanelMode
                isActionable
                inFilterStat
                isCheckLoading={isLocationsStatsLoading}
                //hasTooltip={t`Global Locations: ` + props.locationCount[0]}
                isSelected={isSelectedLocation}
                onClick={() =>
                  setSelectedIdentity(
                    isSelectedLocation
                      ? null
                      : {
                          ruleId,
                          filterId: null,
                          type: 'locations',
                          category,
                        },
                  )
                }
              />
            </PolicyStatContainer>
            <PolicyFilterGroupList
              isDisabled={isDisabled}
              filterSets={_.map(
                assignmentsMatch?.locationsMatch?.anyOf,
                filterGroup => ({
                  id: filterGroup.id,
                  allOf: [...filterGroup.allOf],
                }),
              )}
              config={getLocationFilterConfigCommon(t)}
              selectedId={selectedIdentity?.filterId}
              onAddGroup={addLocationIntersection}
              onDeleteField={removeField}
              onSelectGroup={(id, isSelected) =>
                selectFilterIntersection({
                  ruleId,
                  filterId: id,
                  type: 'locations',
                  category,
                })
              }
              onDeleteGroup={removeIntersection}
            />
          </RuleSectionContainer>
        </div>
      </div>
    </div>
  );
};
export default ProximityPolicyCategory;
